import { Copy, Trash2, ArrowUpRight, Star, AppWindow } from 'lucide-react';

const BUILT_IN_KEYS = new Set(['modern-blue', 'warm-earth', 'dark-pro', 'minimal-gray', 'vibrant-teal']);

const ThemeCard = ({ theme, onEdit, onDuplicate, onDelete, onMakeDefault }) => {
    const isBuiltIn = BUILT_IN_KEYS.has(theme.theme_key);
    const preview = theme.preview || '#6366f1';

    return (
        <div
            onClick={() => onEdit?.(theme)}
            className="group relative bg-white dark:bg-slate-900 grey:bg-zinc-50 rounded-2xl border border-slate-200/80 dark:border-slate-800 grey:border-zinc-200 overflow-hidden cursor-pointer hover:shadow-xl hover:shadow-slate-200/50 dark:hover:shadow-slate-900/50 hover:-translate-y-0.5 transition-all duration-300"
        >
            {/* Color banner */}
            <div className="h-24 relative overflow-hidden">
                <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${preview}, ${preview}99, ${preview}55)` }} />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.08) 100%)' }} />

                {/* Decorative circles */}
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full opacity-20" style={{ background: 'white' }} />
                <div className="absolute -bottom-3 -left-3 w-14 h-14 rounded-full opacity-10" style={{ background: 'white' }} />

                {/* Open indicator */}
                <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <ArrowUpRight size={14} className="text-white" />
                </div>

                {/* Badge */}
                {isBuiltIn && (
                    <div className="absolute bottom-3 left-3.5 flex items-center gap-2">
                        <span className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-white/70 dark:bg-slate-900/70 text-slate-500 dark:text-slate-400 rounded-full">
                            System
                        </span>
                    </div>
                )}
            </div>

            {/* Body */}
            <div className="px-4 pt-3 pb-3.5">
                <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 grey:text-zinc-900 truncate leading-tight">
                            {theme.name}
                        </h3>
                        {theme.is_default && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-medium rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 flex-shrink-0">
                                Platform default
                            </span>
                        )}
                        {theme.app_count > 0 && (
                            <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-medium rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex-shrink-0"
                                title={`Assigned to ${theme.app_count} app${theme.app_count === 1 ? '' : 's'}`}
                            >
                                <AppWindow size={9} strokeWidth={2.5} />
                                {theme.app_count} {theme.app_count === 1 ? 'app' : 'apps'}
                            </span>
                        )}
                    </div>
                    {/* Color dot */}
                    <span className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5 ring-2 ring-white dark:ring-slate-900" style={{ background: preview }} />
                </div>
                <p className="text-xs text-slate-400 dark:text-slate-500 grey:text-zinc-500 line-clamp-1 leading-relaxed">
                    {theme.description || theme.theme_key}
                </p>

                {/* Inline actions — always visible, subtle */}
                <div className="flex items-center gap-1 mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 grey:border-zinc-200">
                    <button
                        onClick={(e) => { e.stopPropagation(); onDuplicate?.(theme.id); }}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-slate-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors"
                        title="Duplicate"
                    >
                        <Copy size={11} /> Duplicate
                    </button>
                    {!isBuiltIn && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDelete?.(theme); }}
                            className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-slate-400 dark:text-slate-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                            title="Delete"
                        >
                            <Trash2 size={11} /> Delete
                        </button>
                    )}
                    {!theme.is_default && onMakeDefault && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onMakeDefault(theme); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors"
                            title="Make Default"
                        >
                            <Star size={11} /> Make Default
                        </button>
                    )}
                    <span className="flex-1" />
                    <span className="text-[9px] font-mono text-slate-300 dark:text-slate-700 group-hover:text-slate-400 dark:group-hover:text-slate-500 transition-colors">
                        {theme.theme_key}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ThemeCard;
